/**
 * Dev-sync J4 — routing manual air-condition edits through the
 * `commit_air_conditions` endpoints when synchronize is on.
 *
 * Legacy gates (both fire an early return BEFORE the plain PATCH):
 *  - section.js:200-284 — a PLENUM or EVAP_PACK section whose changed-set
 *    carries `eadb_temp_summer`/`eawb_temp_summer` (★ only the _summer
 *    aliases — a winter-only edit falls through to the plain PATCH) while
 *    `ahu.synchronize_air_conditions` is on → POST
 *    `sections/<id>/commit_air_conditions` (plenum) or
 *    `evap_packs/<epackId>/commit_air_conditions` (the evap pack's own
 *    endpoint — only it knows how to cascade a media-derived leaving shift,
 *    section.js:196-199).
 *  - component_model.js:155-227 — a USER_DEFINED_ACCESSORY whose changes
 *    carry ANY of the 12 air keys (entering+leaving × bare/_summer/_winter)
 *    → POST `user_defined_accessories/<id>/commit_air_conditions` with the
 *    WHOLE accumulated diff.
 *
 * Both legacy calls pass `autoReload: false` (section.js:226,
 * component_model.js:180): the `changed:section:<id>:eadb_temp:eawb_temp`
 * event drives a live refresh instead; other components stay visually stale
 * until a manual reload — deliberate, keep it.
 *
 * The sync flag: legacy reads the board's own AHU model
 * (`self.ahu.get('synchronize_air_conditions')`, loaded once at page boot —
 * fn/map.js:704-738). React's equivalent single source is the `getAhuUnit`
 * RTK cache; `selectBoardSyncOn` reads it without prop-drilling the unit
 * into every form.
 */

import { store, type AppDispatch, type RootState } from 'app/store';
import { ahuBoardApi } from 'app/services/ahuBoardApi';
import { ahuBoardBus } from '../bus/ahuBoardBus';
import { guardSelect, type CascadeResponse } from './cascadeCommit';

/** section.js:202 — only the _summer entering aliases gate the section path. */
export const SECTION_AIR_COMMIT_KEYS = ['eadb_temp_summer', 'eawb_temp_summer'] as const;

/** component_model.js:155-159 — the UDA trigger set (12 keys: entering AND
 * leaving, bare + both season variants). */
export const UDA_AIR_COMMIT_KEYS = [
  'eadb_temp',
  'eawb_temp',
  'ladb_temp',
  'lawb_temp',
  'eadb_temp_summer',
  'eawb_temp_summer',
  'ladb_temp_summer',
  'lawb_temp_summer',
  'eadb_temp_winter',
  'eawb_temp_winter',
  'ladb_temp_winter',
  'lawb_temp_winter',
] as const;

export function hasAnyKey(changes: Record<string, unknown>, keys: readonly string[]): boolean {
  return keys.some((k) => Object.prototype.hasOwnProperty.call(changes, k));
}

/** The board's AHU id — from the getAhuUnit cache args (exactly one entry
 * per board mount; header-popup contexts have none → null). */
export function boardAhuId(state: RootState): number | null {
  const args = ahuBoardApi.util.selectCachedArgsForQuery(state, 'getAhuUnit');
  return (args[0] as number | undefined) ?? null;
}

/**
 * The board's live synchronize_air_conditions — from the getAhuUnit cache.
 * False when the unit isn't loaded, matching legacy's undefined-model
 * fallbacks (`!!(ahu && ahu.get && …)`).
 */
export function selectBoardSyncOn(state: RootState): boolean {
  const id = boardAhuId(state);
  if (id == null) return false;
  const res = ahuBoardApi.endpoints.getAhuUnit.select(id)(state);
  return !!(res.data as { synchronize_air_conditions?: boolean } | undefined)
    ?.synchronize_air_conditions;
}

// ---------------------------------------------------------------------------
// Selector-side auto-disable (survey §A2: three call sites turn sync OFF —
// manually editing an air-condition field inside the coil / energy-wheel /
// PHX rating dialogs silently disables synchronize for the whole unit).
// ---------------------------------------------------------------------------

/** coil.selector.js:2343 — the coil Select-time trigger pair(s). */
export const COIL_SYNC_TRIGGER_FIELDS = [
  'eadb_temp',
  'eawb_temp',
  'eadb_temp_winter',
  'eawb_temp_winter',
] as const;

/** energy_wheel.selector.js:2333-2340 / plate_heat_exchanger.selector.js:
 * 2236-2243 — the 12 per-season OA/RA model fields whose EDIT (change event,
 * prev-value comparison) disables sync. */
export const SELECTOR_AIR_CONDITION_FIELDS = [
  'oa_db_temp_summer',
  'oa_db_temp_winter',
  'oa_wb_temp_summer',
  'oa_wb_temp_winter',
  'oa_rh_summer',
  'oa_rh_winter',
  'ra_db_temp_summer',
  'ra_db_temp_winter',
  'ra_wb_temp_summer',
  'ra_wb_temp_winter',
  'ra_rh_summer',
  'ra_rh_winter',
] as const;

/** coil.selector.js:2344-2350 — per key: skip if absent from newFields;
 * undefined/null prev counts as changed only when next isn't; else
 * String-compare. */
export function sectionAirFieldsChanged(
  newFields: Record<string, unknown>,
  section: Record<string, unknown>
): boolean {
  return COIL_SYNC_TRIGGER_FIELDS.some((key) => {
    if (!(key in newFields)) return false;
    const prev = section[key];
    const next = newFields[key];
    if (prev === undefined || prev === null) return next !== undefined && next !== null;
    return String(prev) !== String(next);
  });
}

/**
 * The shared OFF write (coil disableSyncOnAhu js:2365-2379 — Backbone
 * save {patch, wait:true}; ewheel/PHX js:2359-2381/2262-2284 — local
 * `ahu.set(false)` first, then a raw fire-and-forget PATCH). Both flavors:
 * cache first (the legacy model set), then the PATCH; the caller awaits
 * (coil) or fire-and-forgets (ewheel/PHX). No error handling — legacy has
 * none on any of the three. Resolves the board unit from the store itself
 * (the selectors don't carry an ahu prop); a header-popup context has no
 * board unit → no-op, matching legacy's `if (!ahu/!ahuId) return`.
 */
export function disableSyncOnAhu(): Promise<void> {
  const state = store.getState();
  const ahuId = boardAhuId(state);
  if (!ahuId) return Promise.resolve();
  (store.dispatch as AppDispatch)(
    ahuBoardApi.util.updateQueryData('getAhuUnit', ahuId, (draft) => {
      (draft as Record<string, unknown>).synchronize_air_conditions = false;
    })
  );
  const token = (document.cookie.match(/(?:^|;\s*)csrftoken=([^;]*)/) || [])[1] || '';
  return fetch(`/api/v1.0/ahu/units/${ahuId}`, {
    method: 'PATCH',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      ...(token ? { 'X-CSRFToken': token } : {}),
    },
    body: JSON.stringify({ synchronize_air_conditions: false }),
  }).then(
    () => undefined,
    () => undefined
  );
}

/**
 * The ewheel/PHX per-edit trigger (disableSyncIfChanged,
 * energy_wheel.selector.js:2342-2357 / plate_heat_exchanger.selector.js:
 * 2245-2260): a genuine value change on one of the 12 per-season OA/RA
 * fields while sync is on → fire-and-forget OFF. `prev === undefined` bails
 * (the legacy no-stashed-prev-value case).
 */
export function disableSyncIfAirFieldEdited(name: string, prev: unknown, next: unknown): void {
  if (!(SELECTOR_AIR_CONDITION_FIELDS as readonly string[]).includes(name)) return;
  if (prev === undefined) return;
  if (String(prev) === String(next)) return;
  if (!selectBoardSyncOn(store.getState())) return;
  void disableSyncOnAhu();
}

interface SectionCommitOpts {
  dispatch: AppDispatch;
  /** plenum → the section's own id; evap pack → the EPACK COMPONENT id
   * (section.js:268-283 resolves components.models[0].id). */
  url: string;
  sectionId: number;
  tunnelId: number | null | undefined;
  changes: Record<string, unknown>;
  /** extra per-caller work after the commit lands (validation etc.). */
  onCommit?: (resp: CascadeResponse) => void;
}

/**
 * The shared POST half of _runGuardedCommit (section.js:205-261): payload is
 * the raw changed-fields snapshot; onCommit merges the change-set into the
 * tunnel section-list cache (legacy's model already carried the values —
 * the cache IS the React model) and fires the eadb/eawb bus event the live
 * consumers listen on (section.js:231-234). onError: legacy only clears its
 * XHR trackers — no revert, model keeps the un-persisted value — so here a
 * failure changes nothing.
 */
export function commitSectionAirConditions(opts: SectionCommitOpts): Promise<CascadeResponse | null> {
  const { dispatch, url, sectionId, tunnelId, changes, onCommit } = opts;
  return guardSelect({
    url,
    payload: changes,
    autoReload: false, // section.js:226
    onCommit: (resp) => {
      if (tunnelId != null) {
        dispatch(
          ahuBoardApi.util.updateQueryData('getTunnelSectionList', tunnelId, (rows) =>
            rows.map((row) => (row.id === sectionId ? { ...row, ...changes } : row))
          )
        );
      }
      if (
        Object.prototype.hasOwnProperty.call(changes, 'eadb_temp_summer') ||
        Object.prototype.hasOwnProperty.call(changes, 'eawb_temp_summer')
      ) {
        ahuBoardBus.emit(`changed:section:${sectionId}:eadb_temp:eawb_temp`);
      }
      onCommit?.(resp);
    },
  });
}
